import type { DataComponentType, MarketsComponentType } from '../types';

type Layout = DataComponentType['layout'];
type LooseComponent = DataComponentType & Record<string, unknown>;
type LooseTab = Record<string, unknown> & {
  id?: string;
  layout?: Layout;
  name?: string | null;
};

const normalizeTab = (tab: LooseTab, layout: Layout) => ({
  ...tab,
  id: tab.id ?? crypto.randomUUID(),
  name: tab.name ?? '',
  layout: tab.layout ?? layout
});

const buildTabsResult = (tabs: LooseTab[], layout: Layout, id: string): MarketsComponentType => ({
  layout,
  tabs: tabs.map(tab => normalizeTab(tab, layout)) as MarketsComponentType['tabs'],
  id
});

const buildNamedSingleTabResult = (
  name: string,
  component: DataComponentType,
  id: string
): MarketsComponentType => ({
  layout: component.layout,
  tabs: [{ name, ...component }] as MarketsComponentType['tabs'],
  id
});

export const parseMarketComponent = (
  component: DataComponentType,
  version = '1'
): MarketsComponentType => {
  const temp = component as LooseComponent;
  const { layout } = component;
  const tabs = temp.tabs as LooseTab[] | undefined;
  const id = crypto.randomUUID();

  if (temp.type === 'tabcontainer' && Array.isArray(tabs)) {
    return buildTabsResult(tabs, layout, id);
  }

  if (version === '1') {
    if (temp.type === 'feed') {
      return buildNamedSingleTabResult('Feed', component, id);
    }
    if (temp.type === 'monthly_recap') {
      return buildNamedSingleTabResult('Monthly recap', component, id);
    }
    if (temp.type === 'file_list') {
      return {
        layout,
        tabs: [{ ...temp, name: temp.name ?? 'Data lake files' }] as MarketsComponentType['tabs'],
        id
      };
    }
    if (Array.isArray(tabs)) {
      return {
        layout,
        tabs: tabs.map(tab => ({ ...tab, ...component })) as MarketsComponentType['tabs'],
        id
      };
    }
  }

  if (temp.type === 'tiles') {
    return {
      layout,
      component: normalizeTab(temp, layout) as MarketsComponentType['component'],
      id
    };
  }

  if (Array.isArray(tabs)) {
    return buildTabsResult(tabs, layout, id);
  }

  // When tabs are not present, build a single tab from the component itself
  return { layout, tabs: [normalizeTab(temp, layout)] as MarketsComponentType['tabs'], id };
};
